import { Button } from '@/components/ui/button';
import Loader from '@/components/ui/Loader';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useStripeOnMount } from '@/provider/stripeLoader';
import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import {
  createSetupIntent,
  fetchInvoices,
  fetchPaymentMethods,
  fetchSubscription,
  fetchUsage,
  setPaymentMethodDefault,
  upgradeSubscription,
} from '@/features/billing/billingThunks';
import { selectPrices } from '@/features/billing/billingSlice';
import { formatPrice, resolvePriceCurrency } from '@/components/billing/usageExpiredUtils';
import { cn } from '@/lib/utils';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

function StripeInputWithLabel({
  label,
  id,
  StripeElement,
  onCompleteChange,
}: {
  label: string;
  id: string;
  StripeElement: typeof CardNumberElement | typeof CardExpiryElement | typeof CardCvcElement;
  onCompleteChange?: (complete: boolean) => void;
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);
  const isActive = isFocused || hasValue;

  return (
    <div className={cn('relative px-4 py-2 h-14 rounded-lg border border-input bg-white')}>
      <label
        className={cn(
          'absolute transition-all duration-200 pointer-events-none text-tertiary',
          isActive ? 'text-sm top-0.5 left-3 px-1' : 'text-base top-1/2 left-4 -translate-y-1/2',
        )}
        htmlFor={id}
      >
        {label}
      </label>
      <div className="pt-4">
        <StripeElement
          id={id}
          options={{
            placeholder: '',
            style: {
              base: {
                color: '#09090a',
                fontSize: '16px',
                fontWeight: '400',
                padding: '8px 8px 8px 8px',
              },
            },
          }}
          onChange={(event) => {
            setHasValue(!event.empty);
            onCompleteChange?.(event.complete);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      </div>
    </div>
  );
}

type UpgradePaymentFormProps = {
  priceId: string | null;
  amount: number | null;
  workspaceId: number | undefined;
  disabled?: boolean;
  onSuccess: () => void;
  onBeforePayment?: () => Promise<boolean>;
};

export default function UpgradePaymentForm({
  priceId,
  amount,
  workspaceId,
  disabled,
  onSuccess,
  onBeforePayment,
}: UpgradePaymentFormProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('billing');
  const stripe = useStripe();
  const elements = useElements();
  useStripeOnMount();
  const user = useAppSelector((s: RootState) => s.auth.user);
  const prices = useAppSelector(selectPrices);
  const workspaceCurrency = useAppSelector(
    (s: RootState) => s.workspace.currentWorkspace?.billingCurrency,
  );
  const currency = resolvePriceCurrency(prices, priceId, workspaceCurrency);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCardNumberComplete, setIsCardNumberComplete] = useState(false);
  const [isCardExpiryComplete, setIsCardExpiryComplete] = useState(false);
  const [isCardCvcComplete, setIsCardCvcComplete] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [isCardholderNameFocused, setIsCardholderNameFocused] = useState(false);

  const isFormValid = isCardNumberComplete && isCardExpiryComplete && isCardCvcComplete;
  const isCardholderNameActive = cardholderName.length > 0 || isCardholderNameFocused;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements || !workspaceId || !priceId) return;

    if (onBeforePayment) {
      const ok = await onBeforePayment();
      if (!ok) return;
    }

    setIsProcessing(true);
    try {
      const setupResult = await dispatch(createSetupIntent(workspaceId)).unwrap();
      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        toast.error(t('upgradePayment.toast.formLoadError'));
        return;
      }

      const billingDetails = {
        name:
          cardholderName.trim() ||
          (user ? `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() : '') ||
          undefined,
        email: user?.email ?? undefined,
      };

      const { setupIntent, error: setupError } = await stripe.confirmCardSetup(
        setupResult.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: billingDetails,
          },
        },
      );

      if (setupError) {
        toast.error(setupError.message ?? t('upgradePayment.toast.saveCardError'));
        return;
      }

      const paymentMethodId =
        typeof setupIntent?.payment_method === 'string'
          ? setupIntent.payment_method
          : (setupIntent?.payment_method as { id?: string })?.id;

      if (paymentMethodId) {
        await dispatch(setPaymentMethodDefault({ workspaceId, paymentMethodId })).unwrap();
      }

      const subscriptionResult = await dispatch(
        upgradeSubscription({ workspaceId, input: { priceId } }),
      ).unwrap();

      if (subscriptionResult.paymentIntentClientSecret && stripe) {
        if (!paymentMethodId) {
          toast.error(t('upgradePayment.toast.noPaymentMethod'));
          return;
        }
        const { error: paymentError } = await stripe.confirmCardPayment(
          subscriptionResult.paymentIntentClientSecret,
          { payment_method: paymentMethodId },
        );
        if (paymentError) {
          const alreadySucceeded =
            paymentError.code === 'payment_intent_unexpected_state' &&
            paymentError.payment_intent?.status === 'succeeded';
          if (!alreadySucceeded) {
            toast.error(paymentError.message ?? t('upgradePayment.toast.paymentFailed'));
            return;
          }
        }
      }

      await Promise.all([
        dispatch(fetchSubscription(workspaceId)),
        dispatch(fetchUsage(workspaceId)),
        dispatch(fetchPaymentMethods(workspaceId)),
        dispatch(fetchInvoices(workspaceId)),
      ]);
      toast.success(t('upgradePayment.toast.planUpdated'));
      onSuccess();
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message;
      toast.error(message ?? t('upgradePayment.toast.error'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <StripeInputWithLabel
        label={t('upgradePayment.cardNumber')}
        id="upgrade-cardNumber"
        StripeElement={CardNumberElement}
        onCompleteChange={setIsCardNumberComplete}
      />

      <div className="flex gap-4">
        <div className="w-1/2">
          <StripeInputWithLabel
            label={t('upgradePayment.expiryDate')}
            id="upgrade-cardExpiry"
            StripeElement={CardExpiryElement}
            onCompleteChange={setIsCardExpiryComplete}
          />
        </div>
        <div className="w-1/2">
          <StripeInputWithLabel
            label={t('upgradePayment.cvv')}
            id="upgrade-cardCvc"
            StripeElement={CardCvcElement}
            onCompleteChange={setIsCardCvcComplete}
          />
        </div>
      </div>

      <div className={cn('relative px-4 py-2 h-14 rounded-lg border border-input bg-white')}>
        <label
          className={cn(
            'absolute transition-all duration-200 pointer-events-none text-tertiary',
            isCardholderNameActive
              ? 'text-sm top-0.5 left-3 px-1'
              : 'text-base top-1/2 left-4 -translate-y-1/2',
          )}
          htmlFor="upgrade-cardholderName"
        >
          {t('upgradePayment.cardholderName')}
        </label>
        <input
          className="w-full text-base focus:outline-none focus:ring-0 focus:border-transparent pt-4 text-black"
          id="upgrade-cardholderName"
          type="text"
          value={cardholderName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCardholderName(e.target.value)}
          onFocus={() => setIsCardholderNameFocused(true)}
          onBlur={() => setIsCardholderNameFocused(false)}
          required
        />
      </div>

      <Button
        type="submit"
        disabled={disabled || !stripe || isProcessing || !isFormValid || !workspaceId || !priceId}
        className="w-full"
      >
        {isProcessing && <Loader />}
        {isProcessing
          ? t('upgradePayment.processing')
          : amount != null
            ? t('unpaid.payAmount', { amount: formatPrice(amount, currency) })
            : t('upgradePayment.pay')}
      </Button>
    </form>
  );
}
